<template>
    <div height="medium" width="large">
        <div class="container">
            <!-- Header -->
            <div class="summary-cards">
                <div class="summary-header">
                    <div class="ask-section">
                        <div class="main-layout">
                            <span class="summary-head">
                                <common-icon name="summary" size="large"></common-icon>
                            </span>
                            <div>
                                <h4 class="summary-title">
                                    {{ $t('modals.patient_profile.profile.summary_detail.summary.title') }}
                                </h4>
                                <p class="summary-subtitle">
                                    {{ $t('modals.patient_profile.profile.summary_detail.summary.subtitle') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Ask Section -->
            <div class="ask-section">
                <div class="main-data">
                    <common-icon name="messages" size="medium"></common-icon>
                    <h4>
                        {{ $t('modals.patient_profile.profile.summary_detail.symptoms.head') }}
                    </h4>
                </div>
                <div class="main-data">
                    <form-cell width="full">
                        <textarea-field
                            :placeholder="$t('modals.patient_profile.profile.summary_detail.symptoms.placeholder')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="message"
                            @change="change($event)"
                        ></textarea-field>
                    </form-cell>
                    <button class="request"><common-icon name="send" size="small"></common-icon></button>
                </div>
            </div>

            <!-- Current Symptoms -->
            <div class="symptom-cards">
                <div class="main">
                    <div class="main-layout">
                        <span class="summary-heading">Q</span>
                        <p class="symptom-question">
                            {{ $t('modals.patient_profile.profile.summary_detail.symptoms.question') }}
                        </p>
                    </div>

                    <span class="symptom-time">
                        {{ $t('modals.patient_profile.profile.summary_detail.symptoms.date') }}
                    </span>
                </div>
                <div class="main-layout">
                    <span class="summary-heading">
                        <common-icon name="summary" size="tiny"></common-icon>
                    </span>
                    <p class="symptom-answer">
                        {{ $t('modals.patient_profile.profile.summary_detail.symptoms.answer') }}
                    </p>
                </div>
            </div>

            <!-- Patient History -->
            <div class="history-section">
                <div class="brief-historys">
                    <div class="main-data">
                        <common-icon name="file" size="small"></common-icon>
                        <h4>{{ $t('modals.patient_profile.profile.summary_detail.brief_history.title') }}</h4>
                    </div>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.history.brief') }}</p>
                </div>
                <div class="past-history">
                    <div class="main-data">
                        <common-icon name="heart" size="small"></common-icon>
                        <h4>{{ $t('modals.patient_profile.profile.summary_detail.past_history.title') }}</h4>
                    </div>
                    <ul>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.history.pastMedical.list_one') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.history.pastMedical.list_two') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.history.pastMedical.list_three') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.history.pastMedical.list_four') }}
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Past Surgical & Allergies -->
            <div class="row">
                <div class="cards">
                    <div class="brief-history">
                        <div class="main-data">
                            <common-icon name="scissors" size="medium"></common-icon>
                            <h4>{{ $t('modals.patient_profile.profile.summary_detail.past_surgical.title') }}</h4>
                        </div>
                    </div>
                    <p class="notifications">{{ $t('modals.patient_profile.profile.summary_detail.surgicalHistory') }}</p>
                </div>
                <div class="cards">
                    <div class="brief-history">
                        <div class="main-data">
                            <common-icon name="insure" size="medium"></common-icon>
                            <h4>{{ $t('modals.patient_profile.profile.summary_detail.allergies_detail.title') }}</h4>
                        </div>
                    </div>
                    <p class="allergy">
                        {{ $t('modals.patient_profile.profile.summary_detail.allergies') }}
                    </p>
                </div>
            </div>

            <!-- Medications & Family History -->
            <div class="row">
                <div class="cards">
                    <div class="brief-history">
                        <div class="main-data">
                            <common-icon name="link" size="medium"></common-icon>
                            <h4>{{ $t('modals.patient_profile.profile.summary_detail.current_detail.title') }}</h4>
                        </div>
                    </div>
                    <div class="medication">
                        <div class="main">
                            <strong class="dose-head">
                                {{ $t('modals.patient_profile.profile.summary_detail.medications.name') }}
                            </strong>
                            <span class="dose">{{ $t('modals.patient_profile.profile.summary_detail.medications.dose') }}</span>
                        </div>
                        <p>Frequency: {{ $t('modals.patient_profile.profile.summary_detail.medications.frequency') }}</p>
                        <p>Indication: {{ $t('modals.patient_profile.profile.summary_detail.medications.indication') }}</p>
                        <p>Started: {{ $t('modals.patient_profile.profile.summary_detail.medications.started') }}</p>
                    </div>
                </div>
                <div class="cards">
                    <div class="brief-history">
                        <div class="main-data">
                            <common-icon name="users" size="medium"></common-icon>
                            <h4>{{ $t('modals.patient_profile.profile.summary_detail.family_detail.title') }}</h4>
                        </div>
                    </div>
                    <ul class="history">
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.familyHistory.list_one') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.familyHistory.list_two') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.familyHistory.list_three') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.familyHistory.list_four') }}
                        </li>
                        <li>
                            {{ $t('modals.patient_profile.profile.summary_detail.familyHistory.list_five') }}
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Social History -->
            <div class="cards wide">
                <div class="brief-history">
                    <div class="main-data">
                        <common-icon name="cup" size="medium"></common-icon>
                        <h4>{{ $t('modals.patient_profile.profile.summary_detail.social_history.title') }}</h4>
                    </div>
                </div>

                <div class="social-grid">
                    <div class="social-item">
                        <strong>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Occupation') }}</strong>
                        <p>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Occupation_value') }}</p>
                    </div>
                    <div class="social-item">
                        <strong>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Smoking') }}</strong>
                        <p>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Smoking_value') }}</p>
                    </div>
                    <div class="social-item">
                        <strong>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Alcohol') }}</strong>
                        <p>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Alcohol_value') }}</p>
                    </div>
                    <div class="social-item">
                        <strong>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Drugs') }}</strong>
                        <p>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Drugs_value') }}</p>
                    </div>
                    <div class="social-item">
                        <strong>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Exercise') }}</strong>
                        <p>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Exercise_value') }}</p>
                    </div>
                    <div class="social-item">
                        <strong>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Marital') }}</strong>
                        <p>{{ $t('modals.patient_profile.profile.summary_detail.socialHistory.Marital_Status') }}</p>
                    </div>
                </div>
            </div>

            <!-- Review of Systems -->
            <div class="cards wide">
                <div class="brief-history">
                    <div class="main-data">
                        <common-icon name="current" size="medium"></common-icon>
                        <h4>{{ $t('modals.patient_profile.profile.summary_detail.review.title') }}</h4>
                    </div>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Constitutional') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Constitutional_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Neurological') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Neurological_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Cardiovascular') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Cardiovascular_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Respiratory') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Respiratory_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Gastrointestinal') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Gastrointestinal_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Genitourinary') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Genitourinary_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Musculoskeletal') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Musculoskeletal_value') }}</p>
                </div>
                <div class="system">
                    <strong>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Dermatologic') }}</strong>
                    <p>{{ $t('modals.patient_profile.profile.summary_detail.reviewOfSystems.Dermatologic_value') }}</p>
                </div>
            </div>

            <div class="cards wide">
                <!-- Title -->
                <div class="brief-history">
                    <div class="main-data">
                        <common-icon name="aala" size="medium"></common-icon>
                        <h4>
                            {{ $t('modals.patient_profile.profile.summary_detail.blood.head') }}
                        </h4>
                    </div>
                    <p class="sign">{{ $t('modals.patient_profile.profile.summary_detail.blood.sign') }}</p>
                </div>
                <!-- Vital Signs -->
                <div class="vital-signs">
                    <div class="vital-box">
                        <p class="vital-label">{{ $t('modals.patient_profile.profile.summary_detail.blood.title') }}</p>
                        <p class="vital-value">{{ $t('modals.patient_profile.profile.summary_detail.blood.subtitle') }}</p>
                    </div>
                    <div class="vital-box">
                        <p class="vital-label">{{ $t('modals.patient_profile.profile.summary_detail.heart_rate.title') }}</p>
                        <p class="vital-value">{{ $t('modals.patient_profile.profile.summary_detail.blood.subtitle') }}</p>
                    </div>
                    <div class="vital-box">
                        <p class="vital-label">{{ $t('modals.patient_profile.profile.summary_detail.temperature.title') }}</p>
                        <p class="vital-value">{{ $t('modals.patient_profile.profile.summary_detail.temperature.subtitle') }}</p>
                    </div>
                    <div class="vital-box">
                        <p class="vital-label">
                            {{ $t('modals.patient_profile.profile.summary_detail.respiratory.title') }}
                        </p>
                        <p class="vital-value">{{ $t('modals.patient_profile.profile.summary_detail.respiratory.subtitle') }}</p>
                    </div>
                    <div class="vital-box">
                        <p class="vital-label">{{ $t('modals.patient_profile.profile.summary_detail.oxygen.title') }}</p>
                        <p class="vital-value">{{ $t('modals.patient_profile.profile.summary_detail.oxygen.subtitle') }}</p>
                    </div>
                </div>

                <hr class="dividing" />

                <!-- Sections -->
                <div class="section">
                    <h4 class="section-title">{{ $t('modals.patient_profile.profile.summary_detail.general.title') }}</h4>
                    <p class="section-text">{{ $t('modals.patient_profile.profile.summary_detail.general.subtitle') }}</p>
                </div>

                <div class="section">
                    <h4 class="section-title">{{ $t('modals.patient_profile.profile.summary_detail.neurological.title') }}</h4>
                    <p class="section-text">
                        {{ $t('modals.patient_profile.profile.summary_detail.neurological.subtitle') }}
                    </p>
                </div>

                <div class="section">
                    <h4 class="section-title">
                        {{ $t('modals.patient_profile.profile.summary_detail.cardiovascular.title') }}
                    </h4>
                    <p class="section-text">
                        {{ $t('modals.patient_profile.profile.summary_detail.cardiovascular.subtitle') }}
                    </p>
                </div>

                <div class="section">
                    <h4 class="section-title">
                        {{ $t('modals.patient_profile.profile.summary_detail.respiratory_detail.title') }}
                    </h4>
                    <p class="section-text">
                        {{ $t('modals.patient_profile.profile.summary_detail.respiratory_detail.subtitle') }}
                    </p>
                </div>

                <div class="section">
                    <h4 class="section-title">{{ $t('modals.patient_profile.profile.summary_detail.abdominal.title') }}</h4>
                    <p class="section-text">{{ $t('modals.patient_profile.profile.summary_detail.abdominal.subtitle') }}</p>
                </div>
            </div>
            <div class="cards wide">
                <!-- Imaging Reports -->
                <div class="section">
                    <div class="brief-history">
                        <div class="main-data">
                            <common-icon name="camera" size="medium"></common-icon>
                            <h4>{{ $t('modals.patient_profile.profile.summary_detail.imaging.head') }}</h4>
                        </div>
                    </div>
                    <!-- Report 1 -->
                    <div class="report-card">
                        <div class="report-header">
                            <h4 class="report-title">
                                {{ $t('modals.patient_profile.profile.summary_detail.imaging.title') }}
                            </h4>
                            <span class="report-date">
                                {{ $t('modals.patient_profile.profile.summary_detail.imaging.subtitle') }}
                            </span>
                        </div>
                        <p class="report-text">
                            <strong>{{ $t('modals.patient_profile.profile.summary_detail.findings.title') }}</strong>
                            {{ $t('modals.patient_profile.profile.summary_detail.imaging.subtitle') }}
                        </p>
                        <p class="report-text">
                            <strong>{{ $t('modals.patient_profile.profile.summary_detail.impression.title') }}</strong>
                            {{ $t('modals.patient_profile.profile.summary_detail.impression.subtitle') }}
                        </p>
                    </div>

                    <!-- Report 2 -->
                    <div class="report-card">
                        <div class="report-header">
                            <h4 class="report-title">
                                {{ $t('modals.patient_profile.profile.summary_detail.emergency.title') }}
                            </h4>
                            <span class="report-date">
                                {{ $t('modals.patient_profile.profile.summary_detail.emergency.subtitle') }}
                            </span>
                        </div>
                        <p class="report-text">
                            <strong>{{ $t('modals.patient_profile.profile.summary_detail.findings_detail.title') }}</strong>
                            {{ $t('modals.patient_profile.profile.summary_detail.findings_detail.subtitle') }}
                        </p>
                        <p class="report-text">
                            <strong>{{ $t('modals.patient_profile.profile.summary_detail.impression_details.title') }}</strong>
                            {{ $t('modals.patient_profile.profile.summary_detail.impression_details.subtitle') }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- Diagnoses -->
            <div class="section">
                <div class="brief-history">
                    <div class="main-data">
                        <common-icon name="alert" size="medium"></common-icon>
                        <h4>{{ $t('modals.patient_profile.profile.summary_detail.diagnoses.head') }}</h4>
                    </div>
                </div>
                <div class="diagnosis-card">
                    <div class="diagnosis-tags">
                        <span class="tag-primary">
                            {{ $t('modals.patient_profile.profile.summary_detail.diagnoses.heading') }}
                        </span>
                        <span class="tag-code">
                            {{ $t('modals.patient_profile.profile.summary_detail.diagnoses.title') }}
                        </span>
                        <span class="tag-active">
                            {{ $t('modals.patient_profile.profile.summary_detail.diagnoses.subtitle') }}
                        </span>
                    </div>
                    <p class="diagnosis-text">
                        {{ $t('modals.patient_profile.profile.summary_detail.diagnoses_details.subtitle') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    export default {
        mixins: [FormMixin, HandleRequestMixin, PageWithDataMixin],
    }
</script>

<style scoped>
    .container {
        margin-top: 20px;
    }
    .sign {
        color: oklch(0.145 0 0);
        margin: 10px 0px;
        font-size: 16px;
        font-weight: 500;
    }
    .main-data {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        width: 100%;
    }
    .main-layout {
        display: flex;
    }
    .system strong {
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        font-size: 14px;
    }
    .summary-head {
        margin-right: 15px;
        width: 2.5rem; /* w-10 = 2.5rem (10 * 0.25rem) */
        height: 2.5rem; /* h-10 = 2.5rem */
        background: linear-gradient(to right, #3b82f6, #8b5cf6); /* from-blue-500 to-purple-600 */
        border-radius: 0.5rem; /* rounded-lg = 0.5rem */
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .summary-heading {
        margin-right: 15px;
        margin-bottom: 10px;
        width: 1.5rem; /* w-10 = 2.5rem (10 * 0.25rem) */
        height: 1.5rem; /* h-10 = 2.5rem */
        background: linear-gradient(to right, #3b82f6, #8b5cf6); /* from-blue-500 to-purple-600 */
        border-radius: 1.5rem; /* rounded-lg = 0.5rem */
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
    }
    .summary-heading i {
        color: #fff;
    }
    .summary-head i {
        color: #fff;
    }
    .request {
        background-color: oklch(0.546 0.245 262.881);
        border-radius: 8px;
        color: #fff;
        padding: 8px;
        opacity: 0.5;
        margin: 0px 10px;
        position: relative;
        top: -17px;
    }

    .vital-signs {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }
    .section-title {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .report-card {
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        padding: 12px;
        margin-bottom: 12px;
        background: #fafafa;
    }

    .report-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
    }

    .report-title {
        font-size: 15px;
        font-weight: bold;
    }

    .report-date {
        font-size: 13px;
        color: #666;
    }

    .report-text {
        font-size: 12px;
        color: oklch(0.373 0.034 259.733);
        margin-bottom: 6px;
        font-weight: 400;
    }

    .diagnosis-card {
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        padding: 12px;
        background: #fafafa;
    }

    .diagnosis-tags {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
    }

    .tag-primary {
        background: #fce7e7;
        color: #b91c1c;
        font-size: 12px;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 500;
    }

    .tag-code {
        background: #f3f4f6;
        color: #333;
        font-size: 12px;
        padding: 2px 6px;
        border-radius: 4px;
    }

    .tag-active {
        background: #d1fae5;
        color: #065f46;
        font-size: 12px;
        padding: 2px 6px;
        border-radius: 4px;
        margin-left: auto;
    }

    .diagnosis-text {
        font-size: 12px;
        font-weight: 500;
        color: oklch(0.21 0.034 264.665);
    }
    .vital-box {
        flex: 1 1 150px;
        border: 1px solid #e0e7ff;
        background: #f0f7ff;
        border-radius: 6px;
        padding: 6px;
        text-align: center;
    }

    .vital-label {
        font-size: 14px;
        color: oklch(0.379 0.146 265.522);
        margin-bottom: 4px;
        font-weight: 500;
    }

    .vital-value {
        font-size: 12px;
        font-weight: 400;
        color: oklch(0.488 0.243 264.376);
    }

    .dividing {
        margin: 16px 0;
        border: none;
        border-top: 1px solid #eee;
    }

    .section {
        margin-bottom: 12px;
    }

    .section-title {
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 4px;
    }

    .section-text {
        color: oklch(0.446 0.03 256.802);
        font-weight: 400;
        font-size: 12px;
        padding: 0px 10px;
    }
    .system p {
        color: oklch(0.446 0.03 256.802);
        font-weight: 400;
        font-size: 12px;
        padding: 12px;
    }
    .summary-cards {
        border-radius: 8px;
        margin-bottom: 15px;
    }

    .summary-header {
        display: flex;
        align-items: center;
    }

    .summary-icon {
        width: 30px;
        margin-right: 10px;
    }

    .summary-title {
        font-size: 16px;
        font-weight: bold;
    }

    .summary-subtitle {
        font-size: 12px;
        color: gray;
        margin-left: 10px;
    }

    .ask-section {
        background: #fff;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #ddd;
        margin-bottom: 15px;
        width: 100%;
    }

    .ask-input {
        width: 100%;
        padding: 8px;
        margin-top: 8px;
        border-radius: 5px;
        border: 1px solid #ccc;
    }

    .symptom-cards {
        background: oklch(0.985 0.002 247.839);
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 15px;
        border: 1px solid #ddd;
    }

    .symptom-question {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 400;
        margin-bottom: 2px;
    }
    .main {
        display: flex;
        justify-content: space-between;
    }
    .symptom-answer {
        margin: 0px;
        font-size: 11px;
        color: oklch(0.373 0.034 259.733);
    }
    .ask-section h4 {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 400;
        margin-left: 10px;
    }
    .symptom-time {
        font-size: 12px;
        color: gray;
    }

    .history-section {
        display: flex;
        gap: 20px;
    }

    .brief-historys,
    .past-history {
        flex: 1;
        background: #fff;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #ddd;
        margin-bottom: 12px;
    }

    .past-history ul li {
        list-style: none;
        color: oklch(0.145 0 0);
        font-size: 12px;
        font-weight: 400;
        margin: 6px 0px;
    }
    .cards ul li {
        list-style: none;
        color: oklch(0.145 0 0);
        font-size: 12px;
        font-weight: 400;
        margin: 6px 0px;
    }
    .row {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;
    }

    .cards {
        flex: 1;
        background: #fff;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #ddd;
    }

    .cards.wide {
        width: 100%;
        margin-bottom: 20px;
    }
    .cards.wide h4 {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 400;
    }
    .brief-historys h4 {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 500;
        margin-left: 13px;
    }
    .past-history ul li::before {
        content: '●';
        color: oklch(0.623 0.214 259.815);
        font-size: 15px;
        margin: 0px 7px;
    }
    .notifications::before {
        content: '●';
        color: oklch(0.627 0.265 303.9);
        font-size: 15px;
        margin: 0px 7px;
    }
    ul.history li::before {
        content: '●';
        color: oklch(0.705 0.213 47.604);
        font-size: 15px;
        margin: 0px 7px;
    }
    .brief-history h4 {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 500;
        margin-left: 13px;
    }
    .brief-history p {
        color: oklch(0.373 0.034 259.733);
        font-size: 12px;
        font-weight: 400;
        margin-bottom: 20px;
    }
    .past-history h4 {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 400;
        margin-left: 13px;
    }
    .cards h4 {
        color: oklch(0.145 0 0);
        font-size: 14px;
        font-weight: 400;
    }
    .allergy {
        color: oklch(0.448 0.119 151.328);
        padding: 8px 12px;
        border: 1px solid;
        border-radius: 10px;
        background-color: oklch(0.982 0.018 155.826);
    }

    .medication {
        border: 1px solid #ddd;
        padding: 10px;
        border-radius: 6px;
        margin-bottom: 10px;
    }
    .medication p {
        font-size: 12px;
        font-weight: 400;
        color: oklch(0.446 0.03 256.802);
        margin: 5px 0px;
    }
    .social-item strong {
        font-size: 14px;
        font-weight: 500;
        color: oklch(0.21 0.034 264.665);
        margin: 5px 0px;
    }
    .social-item p {
        font-size: 12px;
        font-weight: 400;
        color: oklch(0.446 0.03 256.802);
        margin: 5px 0px;
    }
    .dose {
        border: 1px solid #f0f0f0;
        padding: 4px;
        border-radius: 8px;
        margin-left: 10px;
        font-size: 12px;
    }
    .dose-head {
        font-size: 16px;
        font-weight: 500;
        color: oklch(0.21 0.034 264.665);
    }
    .social-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 15px;
    }

    .social-item {
        background: #f9f9f9;
        padding: 10px;
        border-radius: 6px;
    }

    .system {
        margin-bottom: 10px;
        padding: 8px 0;
        border-bottom: 1px solid #eee;
    }
    @media screen and (max-width: 600px) {
        .history-section,
        .row,
        .social-grid {
            display: block !important;
        }
        .vital-signs {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr);
        }
        .vital-label {
            font-size: 13px !important;
        }
        .social-item,
        .cards {
            margin-bottom: 10px !important;
        }
    }
</style>
